import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import ExportToolbar from "../components/ExportToolbar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import {
  Loader2, AlertTriangle, Shield, TrendingUp, Target, BarChart3, Zap,
  ChevronDown, CheckCircle, AlertCircle, Clock, DollarSign, FileCheck, Lightbulb
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EnhancedAIView = ({ contracts }) => {
  const [activeTab, setActiveTab] = useState("delay");
  const [loading, setLoading] = useState(false);
  const [delayResult, setDelayResult] = useState(null);
  const [claimResult, setClaimResult] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [selectedContract, setSelectedContract] = useState("");

  const [delayForm, setDelayForm] = useState({
    project_name: "", sector: "Infrastructure", framework: "FIDIC",
    delivery_model: "EPC", contract_value: "", duration_months: "",
    risk_description: "", weather_zone: "temperate", labor_market: "stable",
    supply_chain_risk: "moderate", regulatory_complexity: "standard", ground_conditions: "known"
  });

  const [claimForm, setClaimForm] = useState({
    project_name: "", sector: "Infrastructure", framework: "FIDIC",
    delivery_model: "EPC", contract_value: "", duration_months: "",
    risk_description: "", payment_history: "on_time", variation_frequency: "low",
    relationship_quality: "good", existing_issues: ""
  });

  const runDelayPrediction = async () => {
    setLoading(true);
    try {
      const payload = { ...delayForm, contract_value: parseFloat(delayForm.contract_value) || null, duration_months: parseInt(delayForm.duration_months) || null };
      const res = await axios.post(`${API}/ai-engine/delay-prediction`, payload);
      setDelayResult(res.data);
      toast.success("Delay analysis complete");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Analysis failed");
    } finally { setLoading(false); }
  };

  const runClaimPrevention = async () => {
    setLoading(true);
    try {
      const payload = {
        ...claimForm,
        contract_value: parseFloat(claimForm.contract_value) || null,
        duration_months: parseInt(claimForm.duration_months) || null,
        existing_issues: claimForm.existing_issues ? claimForm.existing_issues.split(",").map(s => s.trim()) : []
      };
      const res = await axios.post(`${API}/ai-engine/claim-prevention`, payload);
      setClaimResult(res.data);
      toast.success("Claim analysis complete");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Analysis failed");
    } finally { setLoading(false); }
  };

  const runContractAnalysis = async () => {
    if (!selectedContract) { toast.error("Select a contract"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/ai-engine/contract-analysis`, {
        contract_id: selectedContract, analysis_depth: "comprehensive"
      });
      setAnalysisResult(res.data);
      toast.success("Contract analysis complete");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Analysis failed");
    } finally { setLoading(false); }
  };

  const SeverityBadge = ({ level }) => {
    const colors = { critical: "bg-red-100 text-red-700", high: "bg-orange-100 text-orange-700", medium: "bg-amber-100 text-amber-700", low: "bg-green-100 text-green-700" };
    return <Badge className={`${colors[level] || colors.medium} border-none text-xs`}>{level}</Badge>;
  };

  const InputField = ({ label, value, onChange, type = "text", placeholder = "" }) => (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1">{label}</label>
      <input type={type} value={value} onChange={onChange} placeholder={placeholder} className="w-full border-2 border-zinc-200 p-2 text-sm focus:border-black" />
    </div>
  );

  const SelectField = ({ label, value, onChange, options }) => (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1">{label}</label>
      <select value={value} onChange={onChange} className="w-full border-2 border-zinc-200 p-2 text-sm focus:border-black">
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="enhanced-ai-view" data-export-region>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight uppercase font-['Chivo']">AI Analysis Engine</h1>
          <p className="text-zinc-500 mt-1">Advanced AI-powered delay prediction, claim prevention, and contract analysis</p>
        </div>
        <ExportToolbar title="AI Analysis Engine" pdfFilename="ai-analysis" />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-zinc-100 border-2 border-zinc-200">
          <TabsTrigger value="delay" data-testid="tab-delay"><TrendingUp size={14} className="mr-1" /> Delay Prediction</TabsTrigger>
          <TabsTrigger value="claim" data-testid="tab-claim"><Shield size={14} className="mr-1" /> Claim Prevention</TabsTrigger>
          <TabsTrigger value="analysis" data-testid="tab-contract-analysis"><FileCheck size={14} className="mr-1" /> Contract Analysis</TabsTrigger>
        </TabsList>

        <TabsContent value="delay">
          <div className="border-2 border-black brutal-shadow bg-white p-6 space-y-6">
            <h2 className="text-lg font-bold font-['Chivo']">Enhanced Delay Prediction</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <InputField label="Project Name" value={delayForm.project_name} onChange={e => setDelayForm(p => ({ ...p, project_name: e.target.value }))} placeholder="e.g., Highway Bridge Project" />
              <SelectField label="Sector" value={delayForm.sector} onChange={e => setDelayForm(p => ({ ...p, sector: e.target.value }))} options={["Infrastructure", "Energy", "Buildings", "Industrial", "Water", "Mining"]} />
              <SelectField label="Framework" value={delayForm.framework} onChange={e => setDelayForm(p => ({ ...p, framework: e.target.value }))} options={["FIDIC", "NEC", "JCT", "ICC", "AIA", "EJCDC"]} />
              <SelectField label="Delivery Model" value={delayForm.delivery_model} onChange={e => setDelayForm(p => ({ ...p, delivery_model: e.target.value }))} options={["EPC", "Design & Build", "Traditional", "EPCM", "Alliance", "PPP", "BOT"]} />
              <InputField label="Contract Value ($)" type="number" value={delayForm.contract_value} onChange={e => setDelayForm(p => ({ ...p, contract_value: e.target.value }))} placeholder="e.g., 50000000" />
              <InputField label="Duration (months)" type="number" value={delayForm.duration_months} onChange={e => setDelayForm(p => ({ ...p, duration_months: e.target.value }))} placeholder="e.g., 24" />
              <SelectField label="Weather Zone" value={delayForm.weather_zone} onChange={e => setDelayForm(p => ({ ...p, weather_zone: e.target.value }))} options={["temperate", "tropical", "arid", "arctic", "monsoon"]} />
              <SelectField label="Labor Market" value={delayForm.labor_market} onChange={e => setDelayForm(p => ({ ...p, labor_market: e.target.value }))} options={["stable", "tight", "oversupplied", "volatile"]} />
              <SelectField label="Supply Chain Risk" value={delayForm.supply_chain_risk} onChange={e => setDelayForm(p => ({ ...p, supply_chain_risk: e.target.value }))} options={["low", "moderate", "high", "critical"]} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1">Risk Description</label>
              <textarea value={delayForm.risk_description} onChange={e => setDelayForm(p => ({ ...p, risk_description: e.target.value }))} className="w-full border-2 border-zinc-200 p-3 text-sm h-20 focus:border-black" placeholder="Describe key project risks..." />
            </div>
            <Button onClick={runDelayPrediction} disabled={loading || !delayForm.project_name} className="bg-black text-white px-8" data-testid="run-delay-btn">
              {loading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Analyzing...</> : <><Zap size={16} className="mr-2" /> Run Analysis</>}
            </Button>

            {delayResult && (
              <div className="space-y-4 mt-6 pt-6 border-t-2 border-zinc-200">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-zinc-50 border border-zinc-200 text-center">
                    <p className="text-xs font-bold uppercase text-zinc-500">Delay Probability</p>
                    <p className={`text-2xl font-black ${(delayResult.delay_probability || 0) > 0.6 ? 'text-red-600' : (delayResult.delay_probability || 0) > 0.3 ? 'text-amber-600' : 'text-emerald-600'}`}>
                      {((delayResult.delay_probability || 0) * 100).toFixed(0)}%
                    </p>
                  </div>
                  <div className="p-4 bg-zinc-50 border border-zinc-200 text-center">
                    <p className="text-xs font-bold uppercase text-zinc-500">Est. Delay</p>
                    <p className="text-2xl font-black text-[#0047AB]">{delayResult.estimated_delay_days || 0}d</p>
                  </div>
                  <div className="p-4 bg-zinc-50 border border-zinc-200 text-center">
                    <p className="text-xs font-bold uppercase text-zinc-500">Confidence</p>
                    <p className="text-2xl font-black">{((delayResult.confidence_score || 0) * 100).toFixed(0)}%</p>
                  </div>
                  <div className="p-4 bg-zinc-50 border border-zinc-200 text-center">
                    <p className="text-xs font-bold uppercase text-zinc-500">Buffer Needed</p>
                    <p className="text-2xl font-black text-[#FF4F00]">{delayResult.recommended_buffer_days || 0}d</p>
                  </div>
                </div>

                {delayResult.monte_carlo_simulation && (
                  <div className="p-4 bg-blue-50 border border-blue-200">
                    <h4 className="text-xs font-bold uppercase text-blue-600 mb-3">Monte Carlo Simulation</h4>
                    <div className="grid grid-cols-5 gap-3 text-center text-sm">
                      <div><p className="text-xs text-zinc-500">Best</p><p className="font-bold text-emerald-600">{delayResult.monte_carlo_simulation.best_case}d</p></div>
                      <div><p className="text-xs text-zinc-500">P50</p><p className="font-bold">{delayResult.monte_carlo_simulation.p50_delay}d</p></div>
                      <div><p className="text-xs text-zinc-500">P75</p><p className="font-bold text-amber-600">{delayResult.monte_carlo_simulation.p75_delay}d</p></div>
                      <div><p className="text-xs text-zinc-500">P90</p><p className="font-bold text-orange-600">{delayResult.monte_carlo_simulation.p90_delay}d</p></div>
                      <div><p className="text-xs text-zinc-500">Worst</p><p className="font-bold text-red-600">{delayResult.monte_carlo_simulation.worst_case}d</p></div>
                    </div>
                  </div>
                )}

                {delayResult.delay_categories && (
                  <div className="space-y-3">
                    <h4 className="text-xs font-bold uppercase text-zinc-500">Delay Categories</h4>
                    {delayResult.delay_categories.map((cat, i) => (
                      <div key={i} className="p-4 border border-zinc-200">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-sm">{cat.category?.replace(/_/g, ' ')}</span>
                          <div className="flex items-center gap-2">
                            <SeverityBadge level={cat.severity} />
                            <span className="text-sm font-bold">{cat.impact_days}d impact</span>
                          </div>
                        </div>
                        <p className="text-sm text-zinc-600">{cat.description}</p>
                        {cat.mitigation_strategies && (
                          <div className="mt-2 pt-2 border-t border-zinc-100">
                            {cat.mitigation_strategies.map((m, j) => (
                              <div key={j} className="flex items-center gap-2 text-xs mt-1">
                                <Lightbulb size={12} className="text-amber-500" />
                                <span>{m.strategy}</span>
                                <Badge variant="outline" className="text-xs ml-auto">{m.effectiveness}</Badge>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="claim">
          <div className="border-2 border-black brutal-shadow bg-white p-6 space-y-6">
            <h2 className="text-lg font-bold font-['Chivo']">Enhanced Claim Prevention</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <InputField label="Project Name" value={claimForm.project_name} onChange={e => setClaimForm(p => ({ ...p, project_name: e.target.value }))} placeholder="Project name" />
              <SelectField label="Sector" value={claimForm.sector} onChange={e => setClaimForm(p => ({ ...p, sector: e.target.value }))} options={["Infrastructure", "Energy", "Buildings", "Industrial", "Water"]} />
              <SelectField label="Framework" value={claimForm.framework} onChange={e => setClaimForm(p => ({ ...p, framework: e.target.value }))} options={["FIDIC", "NEC", "JCT", "ICC", "AIA", "EJCDC"]} />
              <SelectField label="Delivery Model" value={claimForm.delivery_model} onChange={e => setClaimForm(p => ({ ...p, delivery_model: e.target.value }))} options={["EPC", "Design & Build", "Traditional", "EPCM", "Alliance"]} />
              <InputField label="Contract Value ($)" type="number" value={claimForm.contract_value} onChange={e => setClaimForm(p => ({ ...p, contract_value: e.target.value }))} />
              <InputField label="Duration (months)" type="number" value={claimForm.duration_months} onChange={e => setClaimForm(p => ({ ...p, duration_months: e.target.value }))} />
              <SelectField label="Payment History" value={claimForm.payment_history} onChange={e => setClaimForm(p => ({ ...p, payment_history: e.target.value }))} options={["on_time", "occasional_delays", "frequent_delays", "disputed"]} />
              <SelectField label="Variation Frequency" value={claimForm.variation_frequency} onChange={e => setClaimForm(p => ({ ...p, variation_frequency: e.target.value }))} options={["low", "moderate", "high", "excessive"]} />
              <SelectField label="Relationship Quality" value={claimForm.relationship_quality} onChange={e => setClaimForm(p => ({ ...p, relationship_quality: e.target.value }))} options={["excellent", "good", "strained", "adversarial"]} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1">Risk Description</label>
              <textarea value={claimForm.risk_description} onChange={e => setClaimForm(p => ({ ...p, risk_description: e.target.value }))} className="w-full border-2 border-zinc-200 p-3 text-sm h-20 focus:border-black" placeholder="Describe key risks..." />
            </div>
            <Button onClick={runClaimPrevention} disabled={loading || !claimForm.project_name} className="bg-black text-white px-8" data-testid="run-claim-btn">
              {loading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Analyzing...</> : <><Shield size={16} className="mr-2" /> Run Analysis</>}
            </Button>

            {claimResult && (
              <div className="space-y-4 mt-6 pt-6 border-t-2 border-zinc-200">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-zinc-50 border border-zinc-200 text-center">
                    <p className="text-xs font-bold uppercase text-zinc-500">Overall Risk</p>
                    <SeverityBadge level={claimResult.overall_claim_risk || "medium"} />
                  </div>
                  <div className="p-4 bg-zinc-50 border border-zinc-200 text-center">
                    <p className="text-xs font-bold uppercase text-zinc-500">Risk Score</p>
                    <p className="text-2xl font-black">{claimResult.claim_risk_score || 0}/100</p>
                  </div>
                  <div className="p-4 bg-zinc-50 border border-zinc-200 text-center">
                    <p className="text-xs font-bold uppercase text-zinc-500">Doc Readiness</p>
                    <p className="text-2xl font-black">{claimResult.dispute_resolution_readiness?.documentation_score || 0}%</p>
                  </div>
                </div>

                {claimResult.claim_categories && (
                  <div className="space-y-3">
                    <h4 className="text-xs font-bold uppercase text-zinc-500">Claim Categories</h4>
                    {claimResult.claim_categories.map((cat, i) => (
                      <div key={i} className="p-4 border border-zinc-200">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-sm capitalize">{cat.category?.replace(/_/g, ' ')}</span>
                          <SeverityBadge level={cat.risk_level} />
                        </div>
                        <p className="text-sm text-zinc-600 mb-2">{cat.description}</p>
                        {cat.preventive_measures && (
                          <div className="space-y-1">
                            {cat.preventive_measures.slice(0, 3).map((m, j) => (
                              <div key={j} className="flex items-center gap-2 text-xs">
                                <CheckCircle size={12} className="text-emerald-500" />
                                <span>{m.measure}</span>
                                <Badge variant="outline" className="ml-auto text-xs">{m.priority}</Badge>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {claimResult.executive_summary && (
                  <div className="p-4 bg-blue-50 border border-blue-200">
                    <h4 className="text-xs font-bold uppercase text-blue-600 mb-2">Executive Summary</h4>
                    <p className="text-sm">{claimResult.executive_summary}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="analysis">
          <div className="border-2 border-black brutal-shadow bg-white p-6 space-y-6">
            <h2 className="text-lg font-bold font-['Chivo']">Enhanced Contract Analysis</h2>
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2 block">Select Contract</label>
                <select value={selectedContract} onChange={e => setSelectedContract(e.target.value)} className="w-full border-2 border-zinc-200 p-3 focus:border-black" data-testid="analysis-contract-select">
                  <option value="">Choose a contract...</option>
                  {(contracts || []).map(c => <option key={c.id} value={c.id}>{c.project_name} ({c.framework})</option>)}
                </select>
              </div>
              <Button onClick={runContractAnalysis} disabled={loading || !selectedContract} className="bg-black text-white px-8" data-testid="run-analysis-btn">
                {loading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Analyzing...</> : <><FileCheck size={16} className="mr-2" /> Analyze</>}
              </Button>
            </div>

            {analysisResult && (
              <div className="space-y-4 mt-6 pt-6 border-t-2 border-zinc-200">
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-zinc-50 border text-center">
                    <p className="text-xs font-bold uppercase text-zinc-500">Risk Score</p>
                    <p className={`text-3xl font-black ${(analysisResult.risk_score || 0) > 70 ? 'text-red-600' : 'text-emerald-600'}`}>{analysisResult.risk_score || 0}</p>
                  </div>
                  <div className="p-4 bg-zinc-50 border text-center">
                    <p className="text-xs font-bold uppercase text-zinc-500">Compliance</p>
                    <p className="text-3xl font-black text-[#0047AB]">{analysisResult.compliance_score || 0}</p>
                  </div>
                  <div className="p-4 bg-zinc-50 border text-center">
                    <p className="text-xs font-bold uppercase text-zinc-500">Balance</p>
                    <p className="text-3xl font-black">{analysisResult.balance_score || 0}</p>
                  </div>
                </div>

                {analysisResult.executive_summary && (
                  <div className="p-4 bg-blue-50 border border-blue-200">
                    <h4 className="text-xs font-bold uppercase text-blue-600 mb-2">Executive Summary</h4>
                    <p className="text-sm">{analysisResult.executive_summary}</p>
                  </div>
                )}

                {analysisResult.key_findings && (
                  <div className="space-y-2">
                    <h4 className="text-xs font-bold uppercase text-zinc-500">Key Findings</h4>
                    {analysisResult.key_findings.map((f, i) => (
                      <div key={i} className="p-3 border border-zinc-200 flex items-start gap-3">
                        <SeverityBadge level={f.severity} />
                        <div className="flex-1">
                          <p className="text-sm font-medium">{f.finding}</p>
                          <p className="text-xs text-zinc-500 mt-1">{f.recommendation}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {analysisResult.negotiation_priorities && (
                  <div className="space-y-2">
                    <h4 className="text-xs font-bold uppercase text-zinc-500">Negotiation Priorities</h4>
                    {analysisResult.negotiation_priorities.sort((a, b) => b.priority - a.priority).map((n, i) => (
                      <div key={i} className="p-3 border border-zinc-200">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium">{n.item}</span>
                          <Badge className="bg-[#0047AB] text-white border-none">Priority {n.priority}/10</Badge>
                        </div>
                        <p className="text-xs text-zinc-500">{n.recommended_position}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EnhancedAIView;
